import { useRouteError, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { EmptyState, Flex } from '@kognitos/lattice';
import { getRoutePath } from '@/routes/util';
import { captureException } from '../helpers/capture';
import { isChunkLoadError } from '../helpers/chunk-load-error';

/**
 * Error boundary component for React Router routes.
 * Handles chunk loading errors (after deployments) with a user-friendly message
 * and provides appropriate recovery actions.
 *
 * Note: 404 errors are handled by the catch-all route (`path: '*'`) in the router,
 * so this component only handles actual errors.
 *
 * @returns JSX element with error UI
 */
export function RouteErrorBoundary() {
  const error = useRouteError();
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  // Determine if this is a chunk loading error (common after deployments)
  const isChunkError = isChunkLoadError(error);

  // Capture non-chunk errors to Sentry (chunk errors are expected after deployments)
  if (!isChunkError && error instanceof Error) {
    captureException(error, {
      tags: {
        component: 'RouteErrorBoundary',
        errorType: 'general',
      },
    });
  }

  // For chunk loading errors, show a specific message encouraging reload
  if (isChunkError) {
    return (
      <Flex
        justify="center"
        align="center"
        width="full"
        className="min-h-screen"
      >
        <EmptyState
          icon="ArrowUp"
          title={t('common.error.chunkLoad.title')}
          description={t('common.error.chunkLoad.description')}
          actionLabel={t('common.action.reload')}
          onAction={() => window.location.reload()}
          className="max-w-md"
        />
      </Flex>
    );
  }

  // For all other errors, show generic error message
  return (
    <Flex justify="center" align="center" width="full" className="min-h-screen">
      <EmptyState
        icon="AlertCircle"
        title={t('common.error.title')}
        description={t('common.error.subtitle')}
        actionLabel={t('common.action.goHome')}
        onAction={() => navigate(getRoutePath('HOME'))}
        secondaryActionLabel={t('common.action.reload')}
        onSecondaryAction={() => window.location.reload()}
        className="max-w-md"
      />
    </Flex>
  );
}
